import {
  useDispatchSimulationAllStoresEmail,
  useReapproSimulationAllStoresEmail,
} from '@gozoki/api'
import type { DayOfWeek } from '@gozoki/api-types'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import {
  buildReferenceDateTime,
  DAY_OPTIONS,
  resolveDefaultSimulation,
  type SimulationDefaults,
} from './simulationReferenceDate'

interface ReapproSimulationEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const parseEmails = (raw: string): string[] =>
  raw
    .split(/[,;\r\n]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0)

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (
    error instanceof AxiosError &&
    error.response?.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data
  ) {
    return error.response.data.message as string
  }
  return fallback
}

const formatTimeInputValue = (hour: number, minute: number) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

export const ReapproSimulationEmailDialog = ({
  open,
  onOpenChange,
}: ReapproSimulationEmailDialogProps) => {
  const [emailsRaw, setEmailsRaw] = useState('')
  const [sendToPilotage, setSendToPilotage] = useState(false)
  const [runReappro, setRunReappro] = useState(true)
  const [runDispatch, setRunDispatch] = useState(false)
  const [simulation, setSimulation] = useState<SimulationDefaults>(() => resolveDefaultSimulation())

  const { mutateAsync: requestReapproSimulationAsync, isPending: isRequestingReappro } =
    useReapproSimulationAllStoresEmail()
  const { mutateAsync: requestDispatchSimulationAsync, isPending: isRequestingDispatch } =
    useDispatchSimulationAllStoresEmail()
  const isPending = isRequestingReappro || isRequestingDispatch

  const resetState = () => {
    setEmailsRaw('')
    setSendToPilotage(false)
    setRunReappro(true)
    setRunDispatch(false)
    setSimulation(resolveDefaultSimulation())
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) resetState()
  }

  const handleSubmit = async () => {
    const emails = parseEmails(emailsRaw)

    if (emails.length === 0 && !sendToPilotage) {
      toast.error('Veuillez renseigner au moins un email ou cocher le pilotage Lucie.')
      return
    }
    if (!runReappro && !runDispatch) {
      toast.error('Veuillez sélectionner au moins une simulation (Réappro ou Dispatch).')
      return
    }

    const referenceDate = buildReferenceDateTime(simulation).toISO() ?? undefined
    const payload = { emails, sendToPilotage, referenceDate }
    const tasks: Promise<{ label: string; ok: boolean; message?: string }>[] = []

    if (runReappro) {
      tasks.push(
        requestReapproSimulationAsync(payload)
          .then(() => ({ label: 'Réappro', ok: true }))
          .catch((error: unknown) => ({
            label: 'Réappro',
            ok: false,
            message: extractErrorMessage(
              error,
              'Impossible de lancer la simulation réappro dans toutes les boutiques.',
            ),
          })),
      )
    }

    if (runDispatch) {
      tasks.push(
        requestDispatchSimulationAsync(payload)
          .then(() => ({ label: 'Dispatch', ok: true }))
          .catch((error: unknown) => ({
            label: 'Dispatch',
            ok: false,
            message: extractErrorMessage(
              error,
              'Impossible de lancer la simulation dispatch dans toutes les boutiques.',
            ),
          })),
      )
    }

    const results = await Promise.all(tasks)
    const successes = results.filter((r) => r.ok).map((r) => r.label)
    const failures = results.filter((r) => !r.ok)

    for (const f of failures) {
      toast.error(`${f.label} : ${f.message}`)
    }

    if (successes.length > 0) {
      const plural = successes.length > 1
      toast.success(
        `Simulation${plural ? 's' : ''} ${successes.join(' + ')} en cours de génération.`,
        {
          description: `Vous recevrez un email avec ${plural ? 'les fichiers' : 'le fichier'} dans quelques minutes.`,
        },
      )
    }

    if (failures.length === 0) {
      onOpenChange(false)
      resetState()
    }
  }

  const submitDisabled = isPending || (!runReappro && !runDispatch)

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Simulation Excel toutes boutiques</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium mb-2">Simulations à lancer</p>
            <div className="flex flex-row gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runReappro}
                  onChange={(e) => setRunReappro(e.target.checked)}
                  disabled={isPending}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">Réappro</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runDispatch}
                  onChange={(e) => setRunDispatch(e.target.checked)}
                  disabled={isPending}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">Dispatch</span>
              </label>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Date de simulation</p>
            <div className="flex flex-row gap-2 items-center">
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={simulation.dayOfWeek}
                onChange={(e) =>
                  setSimulation((s) => ({ ...s, dayOfWeek: e.target.value as DayOfWeek }))
                }
                disabled={isPending}
              >
                {DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type="time"
                step={300}
                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={formatTimeInputValue(simulation.hour, simulation.minute)}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':').map(Number)
                  if (Number.isFinite(h) && Number.isFinite(m)) {
                    setSimulation((s) => ({ ...s, hour: h, minute: m }))
                  }
                }}
                disabled={isPending}
              />
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-50"
                onClick={() => setSimulation(resolveDefaultSimulation())}
                disabled={isPending}
              >
                Réinitialiser
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Pré-rempli à aujourd'hui (jeudi le ven/sam/dim car il n'y a pas de production le
              samedi).
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Saisissez les emails destinataires, séparés par des virgules, points-virgules ou retours
            à la ligne.
          </p>

          <textarea
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            rows={3}
            placeholder="email1@exemple.com, email2@exemple.com"
            value={emailsRaw}
            onChange={(event) => setEmailsRaw(event.target.value)}
            disabled={isPending}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendToPilotage}
              onChange={(e) => setSendToPilotage(e.target.checked)}
              disabled={isPending}
              className="w-4 h-4 rounded accent-primary"
            />
            <span className="text-sm">Envoyer à tout le pilotage Lucie</span>
          </label>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
